import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    Card,
    CardContent,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, subDays, format } from 'date-fns';

function maskSIN(sin) {
    const digits = (sin || '').replace(/\D/g, '');
    if (digits.length <= 4) return digits;
    const last4 = digits.slice(-4);
    return `XXX-XX-${last4}`;
}

export default function HOSOnDuty({ onNext }) {
    const defaultStart = subDays(new Date(), 13); // default 14-day period ending today
    const [driverName, setDriverName] = useState('');
    const [sin, setSin] = useState('');
    const [periodStart, setPeriodStart] = useState(defaultStart);
    const [signedAt, setSignedAt] = useState(new Date());
    const [hours, setHours] = useState(() =>
        Array.from({ length: 14 }).map((_, i) => ({
            duty_date: format(addDays(defaultStart, i), 'yyyy-MM-dd'),
            hours_on_duty: ''
        }))
    );
    const [error, setError] = useState(null);
    const [invalidIndices, setInvalidIndices] = useState([]);
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const drawing = useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = canvas.offsetWidth * 2;
        canvas.height = 150 * 2;
        canvas.style.width = '100%';
        canvas.style.height = '150px';
        const ctx = canvas.getContext('2d');
        ctx.scale(2, 2);
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctxRef.current = ctx;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, []);

    // When periodStart changes, update duty_date values
    useEffect(() => {
        setHours((prev) =>
            prev.map((h, i) => ({
                ...h,
                duty_date: format(addDays(periodStart, i), 'yyyy-MM-dd')
            }))
        );
    }, [periodStart]);

    const startDrawing = (e) => {
        drawing.current = true;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };

    const draw = (e) => {
        if (!drawing.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
    };

    const stopDrawing = () => {
        drawing.current = false;
        ctxRef.current.closePath();
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const getSignatureDataUrl = () => {
        const canvas = canvasRef.current;
        return canvas.toDataURL('image/png');
    };

    const handleHoursChange = (index) => (event) => {
        const val = event.target.value;
        // allow empty or numeric with optional decimal
        const sanitized = val === '' ? '' : val;
        setHours((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], hours_on_duty: sanitized };
            return copy;
        });
    };

    const handleSubmit = () => {
        setError(null);
        setInvalidIndices([]);
        // basic field validation
        // if (!driverName.trim()) {
        //     setError('Driver name is required');
        //     return;
        // }
        // if (!sin.trim()) {
        //     setError('SIN is required');
        //     return;
        // }

        // validate signature presence
        // const canvas = canvasRef.current;
        // const ctx = canvas.getContext('2d');
        // const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        // const hasInk = Array.from(imageData).some((v) => v !== 0 && v !== 255);
        // if (!hasInk) {
        //     setError('Signature is required');
        //     return;
        // }

        // // validate hours: required and 0..24
        // const invalid = [];
        // const normalizedHours = hours.map((h, i) => {
        //     const num = h.hours_on_duty === '' ? NaN : Number(h.hours_on_duty);
        //     if (Number.isNaN(num) || num < 0 || num > 24) invalid.push(i);
        //     return { duty_date: h.duty_date, hours_on_duty: Number.isNaN(num) ? null : num };
        // });

        // if (invalid.length > 0) {
        //     setInvalidIndices(invalid);
        //     setError('Please enter valid hours (0–24) for all 14 days.');
        //     return;
        // }

        // const payload = {
        //     driverName: driverName.trim(),
        //     sin: sin.trim(),
        //     sin_masked: maskSIN(sin),
        //     period_start: format(periodStart, 'yyyy-MM-dd'),
        //     hours: normalizedHours,
        //     signatureDataUrl: getSignatureDataUrl(),
        //     signedAt: signedAt ? signedAt.toISOString() : new Date().toISOString(),
        // };

        // if (onNext) onNext(payload);

        onNext()
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Typography variant="h6" gutterBottom>HOS — Hours On Duty or Driving (14 day period)</Typography>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Driver Name"
                                value={driverName}
                                onChange={(e) => setDriverName(e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="SIN"
                                value={sin}
                                onChange={(e) => setSin(e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="Signed At (date & time)"
                                    value={signedAt}
                                    onChange={(newVal) => setSignedAt(newVal)}
                                    renderInput={(params) => <TextField {...params} fullWidth required />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Period Start (first of 14 days)"
                                    value={periodStart}
                                    onChange={(newVal) => setPeriodStart(newVal || defaultStart)}
                                    renderInput={(params) => <TextField {...params} fullWidth required />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* 14-day hours inputs */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>Last 14 days — Hours On Duty</Typography>
                        </Grid>

                        {hours.map((h, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <TextField
                                    fullWidth
                                    label={`${format(addDays(periodStart, i), 'MMM dd, yyyy')}`}
                                    value={h.hours_on_duty}
                                    onChange={handleHoursChange(i)}
                                    type="number"
                                    inputProps={{ min: 0, max: 24, step: 0.25 }}
                                    error={invalidIndices.includes(i)}
                                    helperText={invalidIndices.includes(i) ? '0–24 required' : ''}
                                />
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>Drawn Signature</Typography>
                            <Box
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.2)',
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 1,
                                    background: '#fff'
                                }}
                            >
                                <canvas
                                    ref={canvasRef}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                    style={{ width: '100%', height: 150, touchAction: 'none', background: '#fff' }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Button variant="outlined" onClick={clearSignature}>Clear</Button>
                                    <Button variant="contained" onClick={() => {
                                        const dataUrl = getSignatureDataUrl();
                                        // preview in new tab for quick verification
                                        const w = window.open();
                                        if (w) w.document.write(`<img src="${dataUrl}" />`);
                                    }}>Preview</Button>
                                </Box>
                            </Box>
                        </Grid>

                        {error && (
                            <Grid item xs={12}>
                                <Typography color="error">{error}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Box>
                <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>
                    Continue
                </Button>
            </Box>
        </Box>
    );
}
